import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy, OnInit, viewChild } from '@angular/core'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete'
import { ButtonDirective } from 'primeng/button'
import { IftaLabel } from 'primeng/iftalabel'
import { ToggleSwitch } from 'primeng/toggleswitch'
import { EditorInitEvent, EditorModule } from 'primeng/editor'
import { Divider } from 'primeng/divider'
import { Select, SelectChangeEvent } from 'primeng/select'
import { UnsavedChangesGuard } from '@services/unsaved-changes.guard'
import { ContentUnitManagerService } from '@features/content-unit/services/content-unit-manager.service'
import { PermissionService } from '@core/auth/services/permission.service'
import Quill from 'quill'
import { ContentUnitForm } from '@features/content-unit/models/content-unit.model'
import { InputText } from 'primeng/inputtext'
import { Textarea } from 'primeng/textarea'
import { RouterLink } from '@angular/router'

@Component({
  selector: 'albert-content-unit-display',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AutoComplete,
    ButtonDirective,
    IftaLabel,
    ToggleSwitch,
    EditorModule,
    Divider,
    Select,
    InputText,
    Textarea,
    RouterLink
  ],
  templateUrl: './content-unit-display.component.html',
  styleUrl: './content-unit-display.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentUnitDisplayComponent implements OnInit, OnDestroy {
  public readonly manager = inject(ContentUnitManagerService)
  public readonly permissionService = inject(PermissionService)
  private readonly unsavedGuard = inject(UnsavedChangesGuard)
  private readonly placeholderSelect = viewChild(Select)
  private lastRange: { index: number, length: number } | null = null

  constructor() {
    effect(() => {
      if (this.manager.isReadOnly()) {
        this.contentUnitForm.disable({ emitEvent: false })
      } else {
        this.contentUnitForm.enable({ emitEvent: false })
      }
    })
  }

  protected get contentUnitForm(): FormGroup<ContentUnitForm> {
    return this.manager.contentUnitForm
  }

  protected get highlightedCode(): string {
    const code = this.contentUnitForm.get('content')?.value ?? ''
    const escaped = code.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    return escaped.replaceAll(/##([^#\s]+)##/g, '<span class="code-placeholder">##$1##</span>')
  }

  public ngOnInit(): void {
    this.unsavedGuard.register({
      condition: () => this.contentUnitForm.dirty,
      confirmLeave: () => this.manager.confirmLeave()
    })
  }

  public ngOnDestroy(): void {
    this.unsavedGuard.unregister()
  }

  public onEdit(): void {
    this.manager.isReadOnly.set(false)
    this.manager.contentState.set('edit')
  }

  public onDuplication(): void {
    this.contentUnitForm.patchValue({ uuid: null })
    this.onEdit()
  }

  public onEditorInit(event: EditorInitEvent): void {
    const quill = event.editor as unknown as Quill
    this.manager.quill = quill

    // Quill reports a null range on blur (e.g. when the placeholder select opens); keep the last known cursor position
    quill.on('selection-change', (range: { index: number, length: number } | null) => {
      if (range) {
        this.lastRange = { index: range.index, length: range.length }
      }
    })

    quill.on('text-change', () => {
      const range = quill.getSelection()
      if (range) {
        this.lastRange = { index: range.index, length: range.length }
      }
    })
  }

  public onPlaceholderSelect(event: SelectChangeEvent): void {
    const placeholderId = event.value as string
    if (placeholderId && this.manager.quill) {
      const placeholder = this.manager.placeholders().find(p => p.placeholder === placeholderId)
      const label = placeholder?.label ?? placeholderId
      const range = this.manager.quill.getSelection(true) ?? this.lastRange
      const index = range === null ? this.manager.quill.getLength() : range.index
      this.manager.quill.insertEmbed(index, 'placeholder', { id: placeholderId, label })
      this.manager.quill.setSelection(index + 1)
      this.manager.quill.focus()
      // Clear the select directly so it shows the generic label again and the same placeholder can be picked twice
      this.placeholderSelect()?.writeValue(null)
    }
  }

  public onClose(): void {
    this.manager.close()
  }

  protected onTagsSearch(event: AutoCompleteCompleteEvent): void {
    this.manager.searchTags(event.query)
  }

  protected onCodeScroll(event: Event): void {
    const target = event.target as HTMLElement
    const pre = target.previousElementSibling as HTMLElement | null
    if (pre) {
      pre.scrollTop = target.scrollTop
      pre.scrollLeft = target.scrollLeft
    }
  }
}
